import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  additionalLicenseDocuments,
  coveringDocuments,
  coveringDocumentsForLicense,
  docCue,
  docCueKind,
  docReviewState,
  isExpired,
  nodeAggregateState,
  nodeAggregateStateForLicense,
  reviewStateLabel,
  reviewStatusOf,
  zoneHasLicenseRows,
  zoneLicenseRows,
  zoneLicenseServiceGroups,
  DocReviewState,
  LicenseRequirement,
  LicenseRow,
  NodeAggregateState,
  ZoneLicenseServiceGroup,
} from '../zone-doc.util';
import { slugify } from '../../shared/testid/testid.util';

// A review-action intent emitted in internal mode. The host maps it to a
// reviewStatus (with a reason prompt for reject/revoke) and calls its review
// client — this component never touches a document service.
export type ZoneDocReviewAction = 'approve' | 'reject' | 'revoke' | 'reapprove';

export interface ZoneDocReviewIntent {
  serviceZone: any;
  document: any;
  action: ZoneDocReviewAction;
}

export interface ZoneDocUploadIntent {
  serviceZone: any;
  service: any;
  subType: any | null;
  // The named license requirement the upload row is for (vendor surface). Absent
  // on the internal surface, which still uploads against a service/sub-service
  // node without a license.
  license?: LicenseRequirement | null;
}

export interface ZoneDocDocumentIntent {
  serviceZone: any;
  document: any;
}

// Shared Approach A requirement-row list for ONE service zone: the flat list of
// document-requiring nodes (service-level requirements + required sub-services),
// the multi-document cards per row, the per-zone "Additional documents" bucket,
// and the navy Upload button per row.
//
// A single `mode` input drives the opacity:
//  - vendor   → the aggregate cue only (On file / Under review / Document
//               required) plus the per-document cue; the approve/reject decision
//               is NEVER surfaced. Identical to the shipped vendor Documents tab.
//  - internal → each document additionally shows its full review-state pill
//               (Pending / Approved / Rejected / Expired), surfaces a reject
//               reason, and (when canManage) offers Approve / Reject / re-decide
//               affordances. All mutations are emitted as outputs so the host
//               can wire them to its own client.
@Component({
  standalone: true,
  selector: 'zone-doc-requirements',
  imports: [CommonModule],
  templateUrl: './zone-doc-requirements.component.html',
  styleUrl: './zone-doc-requirements.component.scss',
})
export class ZoneDocRequirementsComponent {
  @Input() serviceZone: any = null;
  @Input() mode: 'vendor' | 'internal' = 'vendor';
  // Internal RBAC: MANAGE sees review/upload/edit/delete actions;
  // MANAGE_READONLY (canManage=false) is view-only (state + download only).
  // Ignored in vendor mode (the vendor always manages their own documents).
  @Input() canManage = true;
  // The applicable license requirements (GET /license-requirements) that drive
  // the requirement rows, plus a serviceTypeId → service `type` bridge
  // (associations key on serviceTypeId; zone nodes key on `type`). Consumed by
  // BOTH surfaces: the vendor tab (pre-filtered to the caller's enabled licenses
  // server-side) and the internal console (filtered to enabled by the host).
  // When licenseRequirements is empty, neither surface shows requirement rows.
  @Input() licenseRequirements: LicenseRequirement[] = [];
  @Input() serviceTypeIdToType: Record<string, string> = {};

  // Mutation intents — the host binds each to its own client (vendor →
  // document.service, internal → admin.service).
  @Output() upload = new EventEmitter<ZoneDocUploadIntent>();
  @Output() editCoverage = new EventEmitter<ZoneDocDocumentIntent>();
  @Output() review = new EventEmitter<ZoneDocReviewIntent>();
  @Output() download = new EventEmitter<any>();
  @Output() preview = new EventEmitter<any>();
  @Output() delete = new EventEmitter<ZoneDocDocumentIntent>();

  get isInternal(): boolean {
    return this.mode === 'internal';
  }

  // Whether internal review/upload/edit/delete affordances render. Vendor mode
  // always shows its own manage actions; internal mode gates them behind MANAGE.
  get showManageActions(): boolean {
    return !this.isInternal || this.canManage;
  }

  // ── Template-facing derivations (delegate to the shared util) ──────────────

  // Both surfaces are license-driven: a zone has rows to render when it has at
  // least one applicable license row.
  zoneHasDocNodes(serviceZone: any): boolean {
    return zoneHasLicenseRows(
      serviceZone,
      this.licenseRequirements,
      this.serviceTypeIdToType,
    );
  }

  // Vendor-surface named requirement rows sourced from the applicable license
  // requirements (one row per attached license under each offered service /
  // sub-service), rendered as a flat list.
  zoneLicenseRows(serviceZone: any): LicenseRow[] {
    return zoneLicenseRows(
      serviceZone,
      this.licenseRequirements,
      this.serviceTypeIdToType,
    );
  }

  // Internal-surface license rows grouped so the service-level rows render on
  // top and the sub-service license rows nest in a bordered group under their
  // parent service — the license-driven replacement for zoneServiceGroups.
  zoneLicenseServiceGroups(serviceZone: any): ZoneLicenseServiceGroup[] {
    return zoneLicenseServiceGroups(
      serviceZone,
      this.licenseRequirements,
      this.serviceTypeIdToType,
    );
  }

  // Aggregate state for a row: license-keyed for a vendor license row, otherwise
  // the node-based state (internal).
  rowAggregateState(node: any): NodeAggregateState {
    if (node?.license) {
      return nodeAggregateStateForLicense(
        this.serviceZone,
        node.service.type,
        node.subType ? node.subType.type : null,
        node.license.id,
      );
    }
    return nodeAggregateState(
      this.serviceZone,
      node.service.type,
      node.subType ? node.subType.type : null,
    );
  }

  // Covering documents for a row: license-keyed for a vendor license row,
  // otherwise the node-based set (internal).
  rowCoveringDocuments(node: any): any[] {
    if (node?.license) {
      return coveringDocumentsForLicense(
        this.serviceZone,
        node.service.type,
        node.subType ? node.subType.type : null,
        node.license.id,
      );
    }
    return coveringDocuments(
      this.serviceZone,
      node.service.type,
      node.subType ? node.subType.type : null,
    );
  }

  nodeAggregateState(
    serviceZone: any,
    serviceTypeKey: string,
    subTypeKey?: string | null,
  ): NodeAggregateState {
    return nodeAggregateState(serviceZone, serviceTypeKey, subTypeKey);
  }

  coveringDocuments(
    serviceZone: any,
    serviceTypeKey: string,
    subTypeKey?: string | null,
  ): any[] {
    return coveringDocuments(serviceZone, serviceTypeKey, subTypeKey);
  }

  // Documents not claimed by any displayed license row — pre-existing docs with a
  // null licenseRequirementId (never auto-attributed) and docs whose license no
  // longer maps to a rendered row. Shared by both surfaces (Decision 10 / the
  // internal additional-documents bucket).
  additionalZoneDocuments(serviceZone: any): any[] {
    return additionalLicenseDocuments(
      serviceZone,
      this.licenseRequirements,
      this.serviceTypeIdToType,
    );
  }

  isExpired(zoneDocument: any): boolean {
    return isExpired(zoneDocument);
  }

  docCue(zoneDocument: any): string {
    return docCue(zoneDocument);
  }

  docCueKind(zoneDocument: any) {
    return docCueKind(zoneDocument);
  }

  // Internal-only per-document state pill.
  docReviewState(zoneDocument: any): DocReviewState {
    return docReviewState(zoneDocument);
  }

  reviewStateLabel(state: DocReviewState): string {
    return reviewStateLabel(state);
  }

  reviewStatusOf(zoneDocument: any): 'PENDING' | 'APPROVED' | 'REJECTED' {
    return reviewStatusOf(zoneDocument);
  }

  // ── testids (flat composite; this component renders once PER zone, so every
  // id is prefixed with the zone key even though a lone instance would not
  // need it — several instances render side-by-side on the Documents page) ──

  get zoneTestKey(): string {
    return slugify(
      this.serviceZone?.nationwide ? 'nationwide' : this.serviceZone?.state ?? 'zone',
    );
  }

  // Stable key for one requirement row: service + sub-service + license id
  // (whichever are present) — never the row's position in the list.
  rowTestKey(node: any): string {
    const parts = [node?.service?.type, node?.subType?.type, node?.license?.id]
      .filter((part) => !!part)
      .map((part) => String(part));
    return slugify(parts.join('-')) || 'row';
  }

  // Stable key for one covering/additional document, keyed on its id.
  docTestKey(doc: any): string {
    return slugify(String(doc?.id ?? doc?.fileName ?? 'document'));
  }

  // ── Emit helpers ───────────────────────────────────────────────────────────

  onUpload(service: any, subType: any, license: any = null): void {
    this.upload.emit({
      serviceZone: this.serviceZone,
      service,
      subType,
      license,
    });
  }

  onEditCoverage(document: any): void {
    this.editCoverage.emit({ serviceZone: this.serviceZone, document });
  }

  onDownload(document: any): void {
    this.download.emit(document);
  }

  // Only PDFs can be previewed in the in-app viewer, so the template gates the
  // eye icon on this — non-PDF documents are download-only.
  isPdf(document: any): boolean {
    return !!document?.fileName && /\.pdf$/i.test(document.fileName);
  }

  onPreview(document: any): void {
    this.preview.emit(document);
  }

  onDelete(document: any): void {
    this.delete.emit({ serviceZone: this.serviceZone, document });
  }

  onReview(document: any, action: ZoneDocReviewAction): void {
    this.review.emit({ serviceZone: this.serviceZone, document, action });
  }
}
